import { observer } from 'mobx-react-lite';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { getWindowInnerHeight, getWindowInnerWidth } from 'src_common/common/mobx-utils/Services/window';

interface AutoPositionPropsType {
    triggerRef: React.RefObject<HTMLElement>;
    className?: string;
    children: React.ReactNode;
}

export const AutoPosition = observer(({ triggerRef, className, children }: AutoPositionPropsType): JSX.Element => {
    const [position, setPosition] = useState<{ top?: number; left?: number; bottom?: number; right?: number }>();
    const [triggerWidth, setTriggerWidth] = useState<number | undefined>();
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const windowsInnerWidth = getWindowInnerWidth() ?? 0;
    const windowsInnerHeight = getWindowInnerHeight() ?? 0;

    const calculatePosition = useCallback(():
        | { top?: number; left?: number; bottom?: number; right?: number; width: number }
        | undefined => {
        if (triggerRef.current !== null && containerRef.current !== null) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            let newPosition: { top?: number; left?: number; bottom?: number; right?: number; width: number } = {
                top: triggerRect.bottom,
                left: triggerRect.left,
                width: triggerRect.width,
            };

            const isSpaceBelow = triggerRect.bottom + containerRect.height <= windowsInnerHeight;
            const isSpaceAbove = triggerRect.top - containerRect.height >= 0;
            const isSpaceRight = triggerRect.left + containerRect.width <= windowsInnerWidth;
            const isSpaceLeft = triggerRect.right - containerRect.width >= 0;

            if (isSpaceBelow) {
                newPosition.top = triggerRect.bottom;
            } else if (isSpaceAbove) {
                newPosition = {
                    bottom: windowsInnerHeight - triggerRect.top,
                    left: triggerRect.left,
                    width: triggerRect.width,
                };
            }

            if (isSpaceRight) {
                newPosition.left = triggerRect.left;
            } else if (isSpaceLeft) {
                newPosition = { ...newPosition, left: triggerRect.right - containerRect.width };
            } else {
                newPosition.left = Math.max(0, (windowsInnerWidth - containerRect.width) / 2);
            }

            return newPosition;
        }
        return undefined;
    }, [triggerRef, windowsInnerWidth, windowsInnerHeight]);

    const updatePosition = useCallback(() => {
        const newPosition = calculatePosition();
        if (newPosition !== undefined) {
            const { width, ...coords } = newPosition;
            setPosition(coords);
            setTriggerWidth(width);
            setIsVisible(true);
        }
    }, [calculatePosition]);

    useEffect(() => {
        updatePosition();
    }, [updatePosition, windowsInnerWidth, windowsInnerHeight]);

    const style: React.CSSProperties & { '--trigger-width'?: string } = {
        ...position,
        opacity: isVisible ? '1' : '0',
        '--trigger-width': triggerWidth === undefined ? undefined : `${triggerWidth}px`,
    };

    return (
        <div
            ref={containerRef}
            style={style}
            className={className}
        >
            {children}
        </div>
    );
});
